import React, { useEffect, useMemo, useState } from "react";
import { Translator } from "./translator";

export default function Maintenance() {
  const { translateText, lang } = Translator();

  const baseIt = useMemo(
    () => ({
      // a11y
      srTitle: "Pagina di manutenzione",
      skip: "Salta al contenuto",

      // hero
      badge: "Manutenzione",
      title: "Stiamo facendo manutenzione",
      subtitle:
        "Il sito è momentaneamente non disponibile. Stiamo lavorando per migliorare l’esperienza.",
      hint: "Riprova tra poco. Se hai urgenza, puoi contattarci.",

      // actions
      refresh: "Ricarica pagina",
      contact: "Contattaci",

      // right card
      systemStatus: "Stato del sistema",
      nowLabel: "Ora",
      whatsHappening: "Cosa sta succedendo?",
      bullet1: "Aggiornamenti e miglioramenti delle prestazioni",
      bullet2: "Controlli di sicurezza e correzioni di stabilità",
      bullet3: "Esperienza migliore su mobile e reti lente",

      // status rows
      labelApi: "API",
      labelDb: "Database",
      labelWebsite: "Sito",
      statusOnline: "online",
      statusOffline: "offline",
      statusMaintenance: "manutenzione",

      // tiny note
      tip:
        "Suggerimento: se stai pubblicando dietro un CDN, invalida la cache dopo la manutenzione.",
    }),
    []
  );

  const [t, setT] = useState(baseIt);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Translate ALL keys from Italian into current lang (it/en/fr/es/any)
        if (!lang || lang === "it") {
          if (!cancelled) setT(baseIt);
          return;
        }

        const entries = Object.entries(baseIt);
        const translatedEntries = await Promise.all(
          entries.map(async ([k, v]) => {
            const out = await translateText(String(v));
            return [k, out || v];
          })
        );

        if (!cancelled) setT(Object.fromEntries(translatedEntries));
      } catch {
        if (!cancelled) setT(baseIt);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, translateText, baseIt]);

  const hardRefresh = () => {
    try {
      window.location.reload();
    } catch {}
  };

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const CONTACT_EMAIL = "emozionartisrl@gmail.com";

  return (
    <main
      className="min-h-[100svh] bg-[#08284D] text-white"
      aria-label={t.srTitle}
    >
      {/* Skip link */}
      <a
        href="#maintenance-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded-lg"
      >
        {t.skip}
      </a>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <section
          id="maintenance-content"
          className="grid gap-8 lg:grid-cols-2 items-center"
        >
          {/* Left */}
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#39FF14]/70 bg-white/5 px-3 py-1 text-sm">
              <span
                className="inline-block h-2 w-2 rounded-full bg-[#39FF14]"
                aria-hidden="true"
              />
              <span className="font-medium">{t.badge}</span>
            </div>

            <h1 className="mt-5 font-[Montserrat] font-bold leading-[1.05] text-[clamp(2rem,6vw,3.5rem)]">
              {t.title}
            </h1>

            <p className="mt-4 text-white/85 text-[clamp(1rem,2.2vw,1.25rem)] leading-relaxed">
              {t.subtitle}
            </p>

            <p className="mt-3 text-white/75 text-sm sm:text-base">{t.hint}</p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
              <button
                type="button"
                onClick={hardRefresh}
                className="inline-flex items-center justify-center rounded-xl bg-[#2E86FF] px-5 py-3 font-semibold shadow-sm transition
                           hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white/70 focus:ring-offset-2 focus:ring-offset-[#08284D]"
              >
                {t.refresh}
              </button>

              <a
                href={`mailto:${encodeURIComponent(CONTACT_EMAIL)}`}
                className="inline-flex items-center justify-center rounded-xl border border-[#39FF14]/60 bg-transparent px-5 py-3 font-semibold
                           transition hover:bg-[#39FF14]/10 focus:outline-none focus:ring-2 focus:ring-[#39FF14]/70 focus:ring-offset-2 focus:ring-offset-[#08284D]"
              >
                {t.contact}
              </a>
            </div>

            <p className="mt-6 text-xs text-white/55">{t.tip}</p>
          </div>

          {/* Right */}
          <div className="relative">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 shadow-lg">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-white/70">{t.systemStatus}</div>
                <div className="text-xs text-white/55 text-right">
                  <span className="sr-only">{t.nowLabel}: </span>
                  {new Date().toLocaleString(lang || "it")}
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <StatusRow
                  label={t.labelApi}
                  value={t.statusOffline}
                  neon
                  animate={!prefersReducedMotion}
                />
                <StatusRow
                  label={t.labelDb}
                  value={t.statusOffline}
                  neon
                  animate={!prefersReducedMotion}
                />
                <StatusRow
                  label={t.labelWebsite}
                  value={t.statusMaintenance}
                  neon={false}
                  animate={!prefersReducedMotion}
                />
              </div>

              <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">{t.whatsHappening}</div>
                <ul className="mt-2 list-disc pl-5 text-sm text-white/75 space-y-1">
                  <li>{t.bullet1}</li>
                  <li>{t.bullet2}</li>
                  <li>{t.bullet3}</li>
                </ul>
              </div>
            </div>

            {/* Decorative glow */}
            <div
              className="pointer-events-none absolute -inset-6 -z-10 blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(57,255,20,0.35), transparent 55%), radial-gradient(circle at 70% 70%, rgba(46,134,255,0.35), transparent 55%)",
              }}
              aria-hidden="true"
            />
          </div>
        </section>
      </div>
    </main>
  );
}

function StatusRow({ label, value, neon, animate }) {
  
  const pillClass =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border " +
    (neon
      ? "border-[#39FF14]/45 bg-[#39FF14]/10 text-white"
      : "border-[#2E86FF]/40 bg-[#2E86FF]/10 text-white");

  const dotClass =
    "h-2 w-2 rounded-full " + (neon ? "bg-[#39FF14]" : "bg-[#2E86FF]");

  return (
    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={dotClass + (animate ? " motion-safe:animate-pulse" : "")}
          aria-hidden="true"
        />
        <span className="text-sm font-semibold truncate">{label}</span>
      </div>
      <span className={pillClass}>{value}</span>
    </div>
  );
}
